'use client';

import * as DS from '@aurorasolar/ds';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { MouseEventHandler } from 'react';

import { useTracking } from '@/analytics';
import { useCrossDomainHref } from '@/app/components';
import { EventData, TrackingEvent } from '@/helpers/tracking';
import { AffiliateId } from '@/models';

type Props = {
  /** attribution for cross-domain funnel links; defaults to the blog affiliate */
  affiliateId?: AffiliateId;
  children?: React.ReactNode;
  event: { data?: EventData; name: TrackingEvent };
  path: string;
} & DS.IButton;

export const ButtonLink = ({
  affiliateId = AffiliateId.AURORA_BLOG,
  children,
  event,
  onClick,
  path,
  variant,
  ...props
}: Props) => {
  const { trackEvent } = useTracking();
  const pathname = usePathname();
  const resolvedPath = useCrossDomainHref(path, affiliateId);

  const handleClick: MouseEventHandler<HTMLButtonElement> = (e) => {
    // @ts-expect-error - unable to use the TrackEventFn overloads
    trackEvent(event.name, { page: pathname, ...event.data });
    onClick?.(e);
  };

  return (
    <DS.Button
      as={Link}
      href={resolvedPath}
      onClick={handleClick}
      // `as={Link}` renders an anchor, which inherits the global link underline on hover; text
      // variants are meant to look like links, so only guard the solid ones
      textDecoration={variant === 'text' ? undefined : { hover: 'none' }}
      variant={variant}
      {...props}
    >
      {children}
    </DS.Button>
  );
};
